import {v4 as uuidv4} from 'uuid';
import {Button, FileInput} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconPaperclip, IconUpload} from '@tabler/icons-react';
import React, {useState} from 'react';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {storeSessionFile} from 'src/offline/files';
import {enqueue} from 'src/offline/outbox';
import type {OfflineSession} from 'src/offline/types';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface FileCaptureCardProps {
  activeSession: OfflineSession | undefined;
}

export const FileCaptureCard: React.FC<FileCaptureCardProps> = ({
  activeSession,
}) => {
  const {setAlert} = useAlert();
  const [file, setFile] = useState<File | null>(null);
  const [isQueuing, setIsQueuing] = useState(false);

  const queueFile = async () => {
    if (!file) return;
    setIsQueuing(true);
    try {
      const refKey = uuidv4();
      await enqueue({
        kind: 'test.echoFile',
        payload: {refKey},
        applyLocal: async ({sessionId}) => {
          await storeSessionFile({
            sessionId,
            kind: 'captured',
            refKey,
            name: file.name,
            contentType: file.type || 'application/octet-stream',
            blob: file,
          });
        },
      });
      setAlert('File captured and echo queued.', 'success');
      setFile(null);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsQueuing(false);
    }
  };

  return (
    <SectionCard title="Capture File">
      <Stack p="md" gap="sm">
        <FileInput
          label="File to capture"
          placeholder="Choose a file"
          leftSection={<IconPaperclip size={16} />}
          value={file}
          onChange={setFile}
          disabled={!activeSession}
          clearable
        />
        <Group gap="sm">
          <Button
            size="sm"
            leftSection={<IconUpload size={16} />}
            onClick={queueFile}
            loading={isQueuing}
            disabled={!activeSession || !file}>
            Capture &amp; Queue Echo
          </Button>
          <Text size="sm" c="dimmed">
            Stores the blob locally, then echoes its metadata on sync.
          </Text>
        </Group>
      </Stack>
    </SectionCard>
  );
};
